import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Copy, Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { getPaymentStatus } from "../lib/api";
import { useLicense } from "../context/LicenseContext";

const MAX_POLLS = 10;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | failed | timeout
  const [license, setLicense] = useState(null);
  const [copied, setCopied] = useState(false);
  const { activate } = useLicense();
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const poll = useCallback(async (attempt) => {
    if (!mountedRef.current) return;
    if (!sessionId) { setState("failed"); return; }
    try {
      const data = await getPaymentStatus(sessionId);
      if (!mountedRef.current) return;
      if (data.payment_status === "paid") {
        setLicense(data.license_key);
        if (data.license_key) activate(data.license_key);
        setState("paid");
        return;
      }
      if (["failed", "expired"].includes(data.payment_status)) {
        setState("failed");
        return;
      }
    } catch (e) { /* retry */ }
    if (!mountedRef.current) return;
    if (attempt >= MAX_POLLS) { setState("timeout"); return; }
    setTimeout(() => poll(attempt + 1), 2000);
  }, [sessionId, activate]);

  useEffect(() => { poll(0); }, [poll]);

  const copyKey = () => {
    navigator.clipboard.writeText(license || "");
    setCopied(true);
    toast.success("License key copied");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-ink text-white min-h-screen flex items-center justify-center px-4" data-testid="payment-success-page">
      <div className="w-full max-w-md bg-surface border border-white/10 rounded-2xl p-8 text-center shadow-2xl">
        {state === "checking" && (
          <>
            <Loader2 size={48} className="mx-auto text-cyan animate-spin" />
            <h1 className="mt-6 font-heading font-bold text-2xl">Confirming your payment…</h1>
            <p className="mt-2 text-sm text-slate-400">Hang tight, this only takes a moment.</p>
          </>
        )}

        {state === "paid" && (
          <div data-testid="payment-paid-state">
            <CheckCircle2 size={56} className="mx-auto text-pass" />
            <h1 className="mt-6 font-heading font-black uppercase tracking-tight text-3xl">You're in.</h1>
            <p className="mt-3 text-sm text-slate-400">
              Thank you for buying Thortek Performance. Your lifetime license is active.
            </p>
            {license && (
              <div className="mt-6 text-start">
                <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">Your license key</div>
                <div className="flex items-center gap-2 bg-ink border border-white/10 rounded-lg px-4 py-3">
                  <code data-testid="license-key" className="font-mono text-sm text-cyan flex-1 truncate">{license}</code>
                  <button onClick={copyKey} data-testid="copy-license-button" className="text-slate-400 hover:text-cyan transition-colors">
                    {copied ? <Check size={16} className="text-pass" /> : <Copy size={16} />}
                  </button>
                </div>
                <p className="mt-2 text-[11px] text-slate-500">Save this key — it's your proof of purchase.</p>
              </div>
            )}
            <Link to="/app" data-testid="success-launch-app"
              className="mt-7 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-cyan text-ink font-heading font-bold uppercase tracking-wide text-sm shadow-xl shadow-cyan/25 hover:bg-cyan-hover transition-all">
              Launch the App <ArrowRight size={18} />
            </Link>
          </div>
        )}

        {(state === "failed" || state === "timeout") && (
          <div data-testid="payment-failed-state">
            <XCircle size={56} className="mx-auto text-fail" />
            <h1 className="mt-6 font-heading font-bold text-2xl">
              {state === "timeout" ? "Still processing" : "Payment not completed"}
            </h1>
            <p className="mt-3 text-sm text-slate-400">
              {state === "timeout"
                ? "Your payment is taking longer than expected. If you were charged, your license will activate shortly."
                : "We couldn't confirm your payment. You have not been charged."}
            </p>
            <Link to="/" data-testid="failed-back-home"
              className="mt-7 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl border border-white/15 text-white font-heading font-bold uppercase tracking-wide text-sm hover:border-cyan/50 hover:text-cyan transition-all">
              Back to home
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
